import React, {
    useEffect,
    useMemo,
    useState
} from 'react';

import axios from 'axios';
import dayjs from 'dayjs';

import {
    Fancybox
} from '@fancyapps/ui/dist/fancybox/';

import '@fancyapps/ui/dist/fancybox/fancybox.css';

import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Descriptions,
    Empty,
    Form,
    Image,
    Input,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Typography,
    message,
    InputNumber
} from 'antd';

import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    FileSearchOutlined,
    ReloadOutlined,
    SearchOutlined,
    SettingOutlined
} from '@ant-design/icons';

import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

import '../styles/CustomerCabinetPage.css';

const {
    RangePicker
} = DatePicker;

const {
    Text,
    Title
} = Typography;

const DEFAULT_DATE_RANGE = [
    dayjs().startOf('month'),
    dayjs().endOf('month')
];

const TASK_STATUS_COLORS = {
    'новое':
        '#8c8c8c',

    'назначено':
        '#1677ff',

    'принято исполнителем':
        '#2f54eb',

    'в дороге на объект':
        '#08979c',

    'на объекте':
        '#d4b106',

    'работы завершены':
        '#d46b08',

    'выполнено (заполнен отчет)':
        '#389e0d',

    'осмечено':
        '#eb2f96',

    'отправлено заказчику':
        '#722ed1',

    'принято заказчиком':
        '#13c2c2',

    'на контроле':
        '#a8071a'
};

const normalizeStatus = value => {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/\s+/g, ' ');
};

const getStatusColor = status => {
    return (
        TASK_STATUS_COLORS[
            normalizeStatus(status)
        ] ||
        '#1677ff'
    );
};

const formatMoney = value => {
    return `${
        Number(value || 0)
            .toLocaleString(
                'ru-RU',
                {
                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2
                }
            )
    } ₽`;
};

const StatusTag = ({
    status
}) => {
    const color =
        getStatusColor(status);

    return (
        <Tag
            style={{
                margin:
                    0,

                color:
                    '#fff',

                background:
                    color,

                borderColor:
                    color,

                fontWeight:
                    600,

                whiteSpace:
                    'normal'
            }}
        >
            {status || 'Без статуса'}
        </Tag>
    );
};

const safeParseJson = value => {
    if (!value) {
        return {};
    }

    if (
        typeof value ===
        'object'
    ) {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return {};
    }
};

const firstNotEmpty = (...values) => {
    return values.find(value => {
        return (
            value !== undefined &&
            value !== null &&
            value !== '' &&
            value !== false &&
            value !== 'false'
        );
    }) ?? null;
};

const getTaskRawData = task => {
    if (!task) {
        return {};
    }

    return safeParseJson(
        task.raw_data ||
        task.rawData
    );
};

const getTaskCustomFields = task => {
    if (!task) {
        return {};
    }

    const rawData =
        getTaskRawData(task);

    return safeParseJson(
        task.custom_fields ||
        task.customFields ||
        rawData.custom_fields ||
        rawData.customFields
    );
};

const getCustomField = (
    task,
    fieldName
) => {
    const customFields =
        getTaskCustomFields(task);

    const key =
        Object.keys(
            customFields
        ).find(item =>
            String(item)
                .toLowerCase() ===
            String(fieldName)
                .toLowerCase()
        );

    if (!key) {
        return null;
    }

    const field =
        customFields[key];

    if (
        field &&
        typeof field ===
            'object' &&
        Object.prototype
            .hasOwnProperty
            .call(
                field,
                'value'
            )
    ) {
        return field.value;
    }

    return field;
};

const getFirstTaskField = (
    task,
    fieldNames = []
) => {
    const rawData =
        getTaskRawData(task);

    for (const fieldName of fieldNames) {
        const directValue =
            firstNotEmpty(
                task?.[fieldName],
                rawData?.[fieldName]
            );

        if (directValue !== null) {
            return directValue;
        }

        const customValue =
            getCustomField(
                task,
                fieldName
            );

        if (customValue !== null) {
            return customValue;
        }
    }

    return null;
};

const formatTaskValue = value => {
    if (
        value === undefined ||
        value === null ||
        value === ''
    ) {
        return '—';
    }

    if (
        typeof value ===
        'object'
    ) {
        return (
            value.name ||
            value.title ||
            value.label ||
            value.value ||
            JSON.stringify(value)
        );
    }

    return String(value);
};

const formatTaskDateTime = value => {
    if (
        value === undefined ||
        value === null ||
        value === '' ||
        value === false ||
        value === 'false'
    ) {
        return '—';
    }

    const text =
        String(value).trim();

    if (!text) {
        return '—';
    }

    /*
     * Unix timestamp в секундах
     * или миллисекундах.
     */
    if (/^\d+$/.test(text)) {
        const numericValue =
            text.length === 10
                ? Number(text) * 1000
                : Number(text);

        const parsed =
            dayjs(numericValue);

        return parsed.isValid()
            ? parsed.format(
                'DD.MM.YYYY HH:mm'
            )
            : '—';
    }

    const parsed =
        dayjs(text);

    return parsed.isValid()
        ? parsed.format(
            'DD.MM.YYYY HH:mm'
        )
        : text;
};

const CustomerCabinetPage = () => {
    const [
        loading,
        setLoading
    ] = useState(false);

    const [
        dateRange,
        setDateRange
    ] = useState(
        DEFAULT_DATE_RANGE
    );

    const [
        filters,
        setFilters
    ] = useState({
        search:
            '',

        status:
            undefined,

        workType:
            undefined,

        ttNumber:
            undefined
    });
    const [
    selectedChartStatuses,
    setSelectedChartStatuses
] = useState([]);



    const [
        report,
        setReport
    ] = useState({
        user:
            null,

        summary: {
            total:
                0,

            estimated:
                0,

            active:
                0,

            completed:
                0,

            incomeAmount:
                0
        },

        tasks:
            [],

        estimatedTasks:
            [],

        charts: {
            daily:
                [],

            statuses:
                []
        },

        options: {
            statuses:
                [],

            workTypes:
                [],

            ttNumbers:
                []
        }
    });

    const [
        selectedTask,
        setSelectedTask
    ] = useState(null);

    const [
        taskModalOpen,
        setTaskModalOpen
    ] = useState(false);

    const [
        taskModalLoading,
        setTaskModalLoading
    ] = useState(false);

    const [
        externalTaskData,
        setExternalTaskData
    ] = useState(null);

    const [
    taskModalData,
    setTaskModalData
] = useState(null);

    const [
    taskPhotoUrls,
    setTaskPhotoUrls
] = useState({});

const [
    taskOriginalUrls,
    setTaskOriginalUrls
] = useState({});

const [
    estimateCorrections,
    setEstimateCorrections
] = useState({});

const [
    estimateComment,
    setEstimateComment
] = useState('');

const [
    estimateSending,
    setEstimateSending
] = useState(false);

const [
    currentEstimateReview,
    setCurrentEstimateReview
] = useState(null);

const [
    estimateReviewHistory,
    setEstimateReviewHistory
] = useState([]);

const [
    profileModalOpen,
    setProfileModalOpen
] = useState(false);

const [
    profileSaving,
    setProfileSaving
] = useState(false);

const [profileForm] =
    Form.useForm();

const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivityAt');
    localStorage.removeItem('lastTokenRefreshAt');

    window.location.href = '/login';
};

const openProfileModal = () => {
    profileForm.setFieldsValue({
        phone:
            report.user?.phone ||
            '',

        email:
            report.user?.email ||
            '',

        currentPassword:
            '',

        password:
            '',

        confirmPassword:
            ''
    });

    setProfileModalOpen(true);
};

const saveProfile = async () => {
    try {
        const values =
            await profileForm.validateFields();

        const token =
            localStorage.getItem(
                'token'
            );

        setProfileSaving(true);

        const payload = {
            phone:
                values.phone?.trim(),

            email:
                values.email?.trim()
        };

        if (
            values.password &&
            values.password.trim()
        ) {
            payload.password =
                values.password;

            payload.currentPassword =
                values.currentPassword;
        }

        const response =
            await axios.put(
                '/api/customer-cabinet/profile',
                payload,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

        const updated =
            response.data ||
            {};

        setReport(prev => ({
            ...prev,
            user: {
                ...(prev.user ||
                    {}),
                ...updated
            }
        }));

        try {
            const stored =
                JSON.parse(
                    localStorage.getItem(
                        'user'
                    ) ||
                        'null'
                );

            if (
                stored &&
                typeof stored ===
                    'object'
            ) {
                localStorage.setItem(
                    'user',
                    JSON.stringify({
                        ...stored,
                        phone:
                            updated.phone ??
                            stored.phone,
                        email:
                            updated.email ??
                            stored.email
                    })
                );
            }
        } catch (_) {
            // ignore localStorage sync errors
        }

        message.success(
            'Данные профиля обновлены'
        );

        setProfileModalOpen(false);
        profileForm.resetFields();
    } catch (error) {
        if (
            error?.errorFields
        ) {
            return;
        }

        console.error(
            'Ошибка обновления профиля:',
            error
        );

        message.error(
            error.response?.data
                ?.message ||
                'Не удалось обновить профиль'
        );
    } finally {
        setProfileSaving(false);
    }
};

const loadProtectedImage =
    async url => {
        const token =
            localStorage.getItem(
                'token'
            );

        const response =
            await axios.get(
                url,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    },

                    responseType:
                        'blob'
                }
            );

        return URL.createObjectURL(
            response.data
        );
    };

    const closeTaskModal = () => {
    Fancybox.close();

    Object
        .values(taskPhotoUrls)
        .forEach(url => {
            if (url?.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        });

    Object
        .values(taskOriginalUrls)
        .forEach(url => {
            if (url?.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        });

    setTaskPhotoUrls({});
    setTaskOriginalUrls({});
    setTaskModalData(null);
    setSelectedTask(null);
    setTaskModalOpen(false);
    setCurrentEstimateReview(null);
    setEstimateReviewHistory([]);
};

const getUserFio = user => {
    if (!user) {
        return '—';
    }

    const fio = [
        user.last_name,
        user.first_name,
        user.middle_name
    ]
        .filter(Boolean)
        .join(' ')
        .trim();

    return (
        fio ||
        user.login ||
        user.email ||
        '—'
    );
};

const ESTIMATE_REVIEW_STATUS_LABELS = {
    draft:
        'Черновик',

    submitted:
        'Отправлено на рассмотрение',

    in_review:
        'На рассмотрении',

    partially_accepted:
        'Принято частично',

    accepted:
        'Принято',

    rejected:
        'Отклонено',

    resolved:
        'Рассмотрено'
};

const ESTIMATE_ITEM_DECISION_LABELS = {
    pending:
        'Ожидает решения',

    accepted:
        'Принято',

    rejected:
        'Отклонено'
};

const getEstimateStatusLabel = status => {
    return (
        ESTIMATE_REVIEW_STATUS_LABELS[
            status
        ] ||
        status ||
        'Неизвестно'
    );
};

const loadEstimateReview = async taskId => {
    try {
        const token =
            localStorage.getItem('token');

        const response = await axios.get(
            `/api/estimate-reviews/task/${taskId}`,
            {
                headers: {
                    Authorization:
                        `Bearer ${token}`
                }
            }
        );

        setCurrentEstimateReview(
            response.data?.review ||
            null
        );

        setEstimateReviewHistory(
            Array.isArray(
                response.data?.history
            )
                ? response.data.history
                : []
        );
    } catch (error) {
        console.error(
            'Ошибка загрузки согласования:',
            error
        );

        setCurrentEstimateReview(null);
        setEstimateReviewHistory([]);
    }
};

    const openTaskModal = async task => {
    const externalId =
        task?.externalId ||
        task?.external_id;

    if (!externalId) {
        message.warning(
            'Не указан внешний ID заявки'
        );

        return;
    }



    /*
     * Открываем окно и очищаем данные
     * предыдущей заявки.
     */
    setTaskOriginalUrls({});
    setSelectedTask(task);
    setEstimateCorrections({});
setEstimateComment('');

if (task?.id) {
    loadEstimateReview(
        task.id
    );
}
    setTaskModalData(null);
    setTaskPhotoUrls({});
    setTaskModalOpen(true);
    setTaskModalLoading(true);

    try {
        const token =
            localStorage.getItem(
                'token'
            );

        /*
         * Получаем подробные данные заявки.
         * Backend возвращает сам объект заявки,
         * без обёртки task.
         */
        const response =
            await axios.get(
                `/api/tasks/ext/${
                    encodeURIComponent(
                        externalId
                    )
                }`,
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

        const modalData =
            response.data ||
            null;

        setTaskModalData(
            modalData
        );

        /*
         * В ответе внешней системы фотографии
         * находятся в массиве attachments.
         */
        const attachments =
            Array.isArray(
                modalData?.attachments
            )
                ? modalData.attachments
                : [];

        const photos =
            attachments
                .filter(attachment => {
                    const extension =
                        String(
                            attachment?.extension ||
                            ''
                        ).toLowerCase();

                    const isPhoto =
                        attachment?.type ===
                            'PHOTO' ||
                        attachment?.semantics ===
                            'PHOTO' ||
                        [
                            'jpg',
                            'jpeg',
                            'png',
                            'webp'
                        ].includes(
                            extension
                        );

                    const isDeleted =
                        attachment?.deleted ===
                            't' ||
                        attachment?.deleted ===
                            true;

                    return (
                        isPhoto &&
                        !isDeleted &&
                        attachment?.num
                    );
                })
                .map(attachment => ({
                    ...attachment,

                    /*
                     * Эти URL обрабатываются вашими
                     * отдельными backend-маршрутами.
                     */
                    preview_url:
                        `/api/tasks/ext/${
                            encodeURIComponent(
                                externalId
                            )
                        }/photos/${
                            encodeURIComponent(
                                attachment.num
                            )
                        }/crop/w400/h300`,

                    original_url:
                        `/api/tasks/ext/${
                            encodeURIComponent(
                                externalId
                            )
                        }/photos/${
                            encodeURIComponent(
                                attachment.num
                            )
                        }`
                }));

        /*
         * Сохраняем подготовленные фотографии
         * внутри данных модального окна.
         */
        setTaskModalData({
            ...modalData,
            photos
        });

        /*
         * Отдельно запрашиваем небольшие превью.
         */
        const previewResults =
            await Promise.allSettled(
                photos.map(
                    async photo => {
                        const blobUrl =
                            await loadProtectedImage(
                                photo.preview_url
                            );

                        return {
                            num:
                                photo.num,

                            url:
                                blobUrl
                        };
                    }
                )
            );

            const originalResults =
    await Promise.allSettled(
        photos.map(
            async photo => {
                const blobUrl =
                    await loadProtectedImage(
                        photo.original_url
                    );

                return {
                    num:
                        photo.num,

                    url:
                        blobUrl
                };
            }
        )
    );

const originals = {};

originalResults.forEach(
    result => {
        if (
            result.status ===
            'fulfilled'
        ) {
            originals[
                result.value.num
            ] =
                result.value.url;
        }
    }
);

setTaskOriginalUrls(
    originals
);

        const previews = {};

        previewResults.forEach(
            result => {
                if (
                    result.status ===
                    'fulfilled'
                ) {
                    previews[
                        result.value.num
                    ] =
                        result.value.url;
                } else {
                    console.error(
                        'Ошибка загрузки превью фотографии:',
                        result.reason
                    );
                }
            }
        );

        setTaskPhotoUrls(
            previews
        );
    } catch (error) {
        console.error(
            'Ошибка загрузки заявки:',
            error
        );

        message.error(
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Не удалось загрузить заявку'
        );
    } finally {
        setTaskModalLoading(false);
    }
};

    const openOriginalPhoto =
    async photo => {
        try {
            const originalUrl =
                await loadProtectedImage(
                    photo.original_url
                );

            Modal.info({
                width:
                    '95vw',

                icon:
                    null,

                closable:
                    true,

                maskClosable:
                    true,

                footer:
                    null,

                content: (
                    <div
                        style={{
                            textAlign:
                                'center'
                        }}
                    >
                        <Image
                            src={
                                originalUrl
                            }
                            preview={
                                false
                            }
                            style={{
                                maxWidth:
                                    '100%',

                                maxHeight:
                                    '85vh',

                                objectFit:
                                    'contain'
                            }}
                        />
                    </div>
                ),

                afterClose: () => {
                    URL.revokeObjectURL(
                        originalUrl
                    );
                }
            });
        } catch (error) {
            message.error(
                'Не удалось загрузить оригинал фотографии'
            );
        }
    };

    const modalTask =
    taskModalData ||
    selectedTask ||
    {};

const modalPhotos =
    Array.isArray(taskModalData?.photos)
        ? taskModalData.photos
        : [];

const incomeInvoices =
    Array.isArray(selectedTask?.incomeInvoices)
        ? selectedTask.incomeInvoices
        : [];

const includedIncomeInvoices =
    incomeInvoices.filter(invoice => {
        return (
            invoice?.include === undefined ||
            invoice?.include === null ||
            invoice?.include === true ||
            invoice?.include === 'true' ||
            Number(invoice?.include) === 1
        );
    });

const estimation = {
    income:
        incomeInvoices,

    income_rows_count:
        incomeInvoices.length,

    included_income_rows_count:
        includedIncomeInvoices.length,

    total_income:
        Number(
            selectedTask?.incomeAmount ||
            incomeInvoices.reduce(
                (sum, invoice) =>
                    sum +
                    Number(
                        invoice?.amount ||
                        (
                            Number(invoice?.price || 0) *
                            Number(invoice?.quantity || 0)
                        ) ||
                        0
                    ),
                0
            )
        )
};

const taskCustomFields =
    getTaskCustomFields(modalTask);

const getVdrFieldValue = fieldName => {
    const field =
        taskCustomFields?.[fieldName];

    if (
        field &&
        typeof field === 'object' &&
        Object.prototype.hasOwnProperty.call(
            field,
            'value'
        )
    ) {
        return field.value;
    }

    return field ?? null;
};

const taskDescription =
    modalTask?.text ||
    '—';

const completedWorksDescription =
    getVdrFieldValue(
        'Opisanie_vepolnenneh_rabot'
    ) ||
    'Нет данных';

const siteAppearanceDate =
    getVdrFieldValue(
        'Vremja_pojavlenija_na_sayte'
    );

const siteLeavingDate =
    getVdrFieldValue(
        'Vremja_pokidanija_sayta'
    );

const executorName =
    modalTask?.assigned_user_fio ||
    '—';

const coexecutorName =
    getVdrFieldValue(
        'FIO_#1'
    ) ||
    '—';

const secondCoexecutorName =
    getVdrFieldValue(
        'FIO_#3'
    ) ||
    '—';







    const requestParams =
        useMemo(() => ({
            dateFrom:
                dateRange?.[0]
                    ?.startOf('day')
                    .toISOString(),

            dateTo:
                dateRange?.[1]
                    ?.endOf('day')
                    .toISOString(),

            search:
                filters.search ||
                undefined,

            status:
                filters.status,

            workType:
                filters.workType,

            ttNumber:
                filters.ttNumber
        }), [
            dateRange,
            filters
        ]);

    const fetchReport =
        async () => {
            setLoading(true);

            try {
                const token =
                    localStorage.getItem(
                        'token'
                    );

                const response =
                    await axios.get(
                        '/api/customer-cabinet/report',
                        {
                            params:
                                requestParams,

                            headers: {
                                Authorization:
                                    `Bearer ${token}`
                            }
                        }
                    );

                setReport({
                    user:
                        response.data?.user ||
                        null,

                    summary:
                        response.data?.summary ||
                        {},

                    tasks:
                        response.data?.tasks ||
                        [],

                    estimatedTasks:
                        response.data
                            ?.estimatedTasks ||
                        [],

                    charts:
                        response.data?.charts ||
                        {
                            daily:
                                [],

                            statuses:
                                []
                        },

                    options:
                        response.data?.options ||
                        {
                            statuses:
                                [],

                            workTypes:
                                [],

                            ttNumbers:
                                []
                        }
                });
            } catch (error) {
                console.error(
                    'Ошибка загрузки кабинета заказчика:',
                    error
                );

                message.error(
                    error.response?.data?.message ||
                    'Не удалось загрузить кабинет заказчика'
                );
            } finally {
                setLoading(false);
            }
        };

    useEffect(() => {
        fetchReport();
    }, [
        dateRange,
        filters.status,
        filters.workType,
        filters.ttNumber
    ]);

    useEffect(() => {
    Fancybox.bind(
        '[data-fancybox="task-gallery"]',
        {
            animated: true,

            dragToClose:
                true,

            placeFocusBack:
                false
        }
    );

    return () => {
        Fancybox.unbind(
            '[data-fancybox="task-gallery"]'
        );

        Fancybox.close();
    };
}, [
    modalPhotos,
    taskPhotoUrls
]);

const updateEstimateCorrection = (
    invoice,
    field,
    value
) => {
    setEstimateCorrections(
        previous => ({
            ...previous,

            [invoice.id]: {
                invoiceId:
                    invoice.id,

                proposedQuantity:
                    previous[
                        invoice.id
                    ]?.proposedQuantity ??
                    null,

                proposedPrice:
                    previous[
                        invoice.id
                    ]?.proposedPrice ??
                    null,

                comment:
                    previous[
                        invoice.id
                    ]?.comment ||
                    '',

                [field]:
                    value
            }
        })
    );
};

const submitEstimateCorrections =
    async () => {
        const items =
            Object.values(
                estimateCorrections
            ).filter(item => {
                return (
                    item.proposedQuantity !==
                        null ||
                    item.proposedPrice !==
                        null ||
                    String(
                        item.comment ||
                        ''
                    ).trim()
                );
            });

        if (
            !items.length &&
            !estimateComment.trim()
        ) {
            message.warning(
                'Укажите хотя бы одну корректировку или комментарий'
            );

            return;
        }

        setEstimateSending(true);

        try {
            const token =
                localStorage.getItem(
                    'token'
                );

            await axios.post(
                `/api/estimate-reviews/task/${
                    selectedTask.id
                }`,
                {
                    externalTaskId:
                        selectedTask.externalId ||
                        selectedTask.external_id,

                    comment:
                        estimateComment,

                    items
                },
                {
                    headers: {
                        Authorization:
                            `Bearer ${token}`
                    }
                }
            );

            message.success(
                'Замечания по смете отправлены'
            );

            setEstimateCorrections({});
            setEstimateComment('');

            await loadEstimateReview(
                selectedTask.id
            );
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                'Не удалось отправить замечания'
            );
        } finally {
            setEstimateSending(false);
        }
    };

    const resetFilters = () => {
        setFilters({
            search:
                '',

            status:
                undefined,

            workType:
                undefined,

            ttNumber:
                undefined
        });

        setDateRange(
            DEFAULT_DATE_RANGE
        );
    };

   const dailyChartData = useMemo(() => {
    const tasks = Array.isArray(report.tasks)
        ? report.tasks
        : [];

    /*
     * Если статусы не выбраны,
     * показываем все доступные статусы.
     */
    const chartStatuses =
        selectedChartStatuses.length
            ? selectedChartStatuses
            : report.charts.statuses.map(
                item => item.status
            );

    /*
     * Ключи для полей объекта графика.
     * Например:
     * status_новое
     * status_назначено
     */
    const getStatusDataKey = status => {
        return `status_${normalizeStatus(status)}`;
    };

    const countsByDate = {};

    tasks.forEach(task => {
        const taskStatus =
            normalizeStatus(
                task.status
            );

        const matchedStatus =
            chartStatuses.find(status => {
                return (
                    normalizeStatus(status) ===
                    taskStatus
                );
            });

        if (!matchedStatus) {
            return;
        }

        const sourceDate =
            task.createdAt ||
            task.created_at ||
            task.date;

        const parsedDate =
            dayjs(sourceDate);

        if (!parsedDate.isValid()) {
            return;
        }

        const dateKey =
            parsedDate.format(
                'YYYY-MM-DD'
            );

        if (!countsByDate[dateKey]) {
            countsByDate[dateKey] = {
                date: dateKey
            };

            chartStatuses.forEach(status => {
                countsByDate[dateKey][
                    getStatusDataKey(status)
                ] = 0;
            });
        }

        const statusDataKey =
            getStatusDataKey(
                matchedStatus
            );

        countsByDate[dateKey][
            statusDataKey
        ] += 1;
    });

    const startDate =
        dateRange?.[0]
            ? dayjs(dateRange[0])
                .startOf('day')
            : null;

    const endDate =
        dateRange?.[1]
            ? dayjs(dateRange[1])
                .startOf('day')
            : null;

    /*
     * Если диапазон дат не задан,
     * возвращаем имеющиеся дни по порядку.
     */
    if (
        !startDate?.isValid() ||
        !endDate?.isValid()
    ) {
        return Object
            .values(countsByDate)
            .sort((first, second) => {
                return (
                    dayjs(first.date)
                        .valueOf() -
                    dayjs(second.date)
                        .valueOf()
                );
            });
    }

    const result = [];

    let currentDate =
        startDate.clone();

    while (
        currentDate.isBefore(
            endDate
        ) ||
        currentDate.isSame(
            endDate,
            'day'
        )
    ) {
        const dateKey =
            currentDate.format(
                'YYYY-MM-DD'
            );

        const row = {
    date: dateKey,
    total: 0
};

chartStatuses.forEach(status => {
    const statusDataKey =
        getStatusDataKey(status);

    const statusCount =
        countsByDate[dateKey]?.[
            statusDataKey
        ] || 0;

    row[statusDataKey] =
        statusCount;

    row.total +=
        statusCount;
});

        result.push(row);

        currentDate =
            currentDate.add(
                1,
                'day'
            );
    }

    return result;
}, [
    report.tasks,
    report.charts.statuses,
    selectedChartStatuses,
    dateRange
]);


const displayedChartStatuses = useMemo(() => {
    if (selectedChartStatuses.length) {
        return selectedChartStatuses;
    }

    return Array.isArray(
        report.charts.statuses
    )
        ? report.charts.statuses.map(
            item => item.status
        )
        : [];
}, [
    selectedChartStatuses,
    report.charts.statuses
]);

const getStatusDataKey = status => {
    return `status_${normalizeStatus(status)}`;
};
    const taskColumns = [
        {
            title:
                'ID заявки',

            dataIndex:
                'externalId',

            key:
                'externalId',

            width:
                110,

            fixed:
                'left'
        },
        {
            title:
                'Дата',

            dataIndex:
                'createdAt',

            key:
                'createdAt',

            width:
                150,

            render:
                value =>
                    value
                        ? dayjs(value)
                            .format(
                                'DD.MM.YYYY HH:mm'
                            )
                        : '—'
        },
        {
            title:
                'Номер ТТ',

            dataIndex:
                'ttNumber',

            key:
                'ttNumber',

            width:
                130,

            render:
                value =>
                    value || '—'
        },
        {
            title:
                'БС',

            dataIndex:
                'bsNumber',

            key:
                'bsNumber',

            width:
                110,

            render:
                value =>
                    value || '—'
        },
        {
            title:
                'Вид работ',

            dataIndex:
                'workType',

            key:
                'workType',

            width:
                190,

            render:
                value =>
                    value || '—'
        },
        {
            title:
                'Заявка',

            dataIndex:
                'title',

            key:
                'title',

            width:
                300
        },
        {
            title:
                'Статус',

            dataIndex:
                'status',

            key:
                'status',

            width:
                210,

            render:
                value => (
                    <StatusTag
                        status={
                            value
                        }
                    />
                )
        },
        {
            title:
                'Действия',

            key:
                'actions',

            width:
                120,

            fixed:
                'right',

            render:
                (_, task) => (
                    <Button
                        type="link"
                        icon={
                            <FileSearchOutlined />
                        }
                        onClick={() => {
                            openTaskModal(task)
                        }}
                    >
                        Открыть
                    </Button>
                )
        }
    ];

    const estimatedTaskColumns = [
        ...taskColumns.slice(0, 7),

        {
            title: 'Стоимость работ',
            dataIndex: 'incomeAmount',
            key: 'incomeAmount',
            width: 150,
            align: 'right',
            render: value => formatMoney(value)
        },

        taskColumns[7]
    ];

    const incomeColumns = [
       
        {
            title:
                'Наименование',

            dataIndex:
                'rate_name',

            key:
                'rate_name',

            width:
                320
        },
        {
            title:
                'Кол-во',

            dataIndex:
                'quantity',

            key:
                'quantity',

            width:
                100,

            align:
                'right'
        },
        {
            title:
                'Ед.',

            dataIndex:
                'unit',

            key:
                'unit',

            width:
                80
        },
        {
            title:
                'Цена',

            dataIndex:
                'price',

            key:
                'price',

            width:
                130,

            align:
                'right',

            render:
                formatMoney
        },
        {
            title:
                'Сумма',

            dataIndex:
                'amount',

            key:
                'amount',

            width:
                140,

            align:
                'right',

            render:
                value => (
                    <Text strong>
                        {formatMoney(value)}
                    </Text>
                )
        },
        {
            title:
                'Примечание',

            dataIndex:
                'note',

            key:
                'note',

            width:
                220,

            render:
                value =>
                    value || '—'
        }
    ];

    const customerEstimateColumns = [
    ...incomeColumns,

    {
        title:
            'Ваше количество',

        key:
            'proposedQuantity',

        width:
            150,

        render:
            (_, invoice) => (
                <InputNumber
                    min={0}
                    precision={4}
                    style={{
                        width:
                            '100%'
                    }}
                    value={
                        estimateCorrections[
                            invoice.id
                        ]?.proposedQuantity
                    }
                    placeholder={
                        String(
                            invoice.quantity ??
                            ''
                        )
                    }
                    onChange={value =>
                        updateEstimateCorrection(
                            invoice,
                            'proposedQuantity',
                            value
                        )
                    }
                />
            )
    },

    {
        title:
            'Ваша цена',

        key:
            'proposedPrice',

        width:
            150,

        render:
            (_, invoice) => (
                <InputNumber
                    min={0}
                    precision={2}
                    style={{
                        width:
                            '100%'
                    }}
                    value={
                        estimateCorrections[
                            invoice.id
                        ]?.proposedPrice
                    }
                    placeholder={
                        String(
                            invoice.price ??
                            ''
                        )
                    }
                    onChange={value =>
                        updateEstimateCorrection(
                            invoice,
                            'proposedPrice',
                            value
                        )
                    }
                />
            )
    },

    {
        title:
            'Комментарий',

        key:
            'customerComment',

        width:
            280,

        render:
            (_, invoice) => (
                <Input.TextArea
                    autoSize={{
                        minRows: 1,
                        maxRows: 4
                    }}
                    value={
                        estimateCorrections[
                            invoice.id
                        ]?.comment ||
                        ''
                    }
                    placeholder="С чем вы не согласны"
                    onChange={event =>
                        updateEstimateCorrection(
                            invoice,
                            'comment',
                            event.target.value
                        )
                    }
                />
            )
    }
];

const toggleChartStatus = status => {
    setSelectedChartStatuses(previous => {
        const normalizedStatus =
            normalizeStatus(status);

        const exists =
            previous.some(item => {
                return (
                    normalizeStatus(item) ===
                    normalizedStatus
                );
            });

        if (exists) {
            return previous.filter(item => {
                return (
                    normalizeStatus(item) !==
                    normalizedStatus
                );
            });
        }

        return [
            ...previous,
            status
        ];
    });
};

    return (
        <div className="customer-cabinet-page">
            <div className="customer-cabinet-container">
                <div className="customer-cabinet-header">
                    <div>
                        <Title
                            level={2}
                            style={{
                                marginBottom:
                                    4
                            }}
                        >
                            Кабинет заказчика
                        </Title>

                        <Text type="secondary">
                            {report.user?.fullName ||
                                report.user?.login ||
                                'Пользователь'}
                        </Text>
                    </div>

                    <Space>
    <Button
        icon={<ReloadOutlined />}
        onClick={fetchReport}
        loading={loading}
    >
        Обновить
    </Button>

    <Button
        icon={<SettingOutlined />}
        onClick={openProfileModal}
    >
        Профиль
    </Button>

    <Button
        danger
        onClick={logout}
    >
        Выйти
    </Button>
</Space>
                </div>

                <Modal
                    title="Профиль"
                    open={profileModalOpen}
                    onCancel={() => {
                        if (
                            !profileSaving
                        ) {
                            setProfileModalOpen(
                                false
                            );
                        }
                    }}
                    onOk={saveProfile}
                    confirmLoading={
                        profileSaving
                    }
                    okText="Сохранить"
                    cancelText="Отмена"
                    destroyOnClose
                    maskClosable={
                        !profileSaving
                    }
                >
                    <Form
                        form={
                            profileForm
                        }
                        layout="vertical"
                        requiredMark={
                            false
                        }
                    >
                        <Form.Item
                            label="Телефон"
                            name="phone"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        'Укажите номер телефона'
                                }
                            ]}
                        >
                            <Input
                                placeholder="+7..."
                                maxLength={
                                    32
                                }
                            />
                        </Form.Item>

                        <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        'Укажите email'
                                },
                                {
                                    type:
                                        'email',
                                    message:
                                        'Некорректный email'
                                }
                            ]}
                        >
                            <Input
                                placeholder="name@example.com"
                                maxLength={
                                    120
                                }
                            />
                        </Form.Item>

                        <Form.Item
                            label="Текущий пароль"
                            name="currentPassword"
                            extra="Нужен только при смене пароля"
                            dependencies={[
                                'password'
                            ]}
                            rules={[
                                ({
                                    getFieldValue
                                }) => ({
                                    validator(
                                        _,
                                        value
                                    ) {
                                        const nextPassword =
                                            getFieldValue(
                                                'password'
                                            );

                                        if (
                                            !nextPassword
                                        ) {
                                            return Promise.resolve();
                                        }

                                        if (
                                            value
                                        ) {
                                            return Promise.resolve();
                                        }

                                        return Promise.reject(
                                            new Error(
                                                'Укажите текущий пароль'
                                            )
                                        );
                                    }
                                })
                            ]}
                        >
                            <Input.Password
                                autoComplete="current-password"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Новый пароль"
                            name="password"
                            rules={[
                                {
                                    min: 6,
                                    message:
                                        'Не короче 6 символов'
                                }
                            ]}
                        >
                            <Input.Password
                                autoComplete="new-password"
                                placeholder="Оставьте пустым, чтобы не менять"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Подтверждение пароля"
                            name="confirmPassword"
                            dependencies={[
                                'password'
                            ]}
                            rules={[
                                ({
                                    getFieldValue
                                }) => ({
                                    validator(
                                        _,
                                        value
                                    ) {
                                        const nextPassword =
                                            getFieldValue(
                                                'password'
                                            );

                                        if (
                                            !nextPassword
                                        ) {
                                            return Promise.resolve();
                                        }

                                        if (
                                            value ===
                                            nextPassword
                                        ) {
                                            return Promise.resolve();
                                        }

                                        return Promise.reject(
                                            new Error(
                                                'Пароли не совпадают'
                                            )
                                        );
                                    }
                                })
                            ]}
                        >
                            <Input.Password
                                autoComplete="new-password"
                            />
                        </Form.Item>
                    </Form>
                </Modal>

                <Card className="customer-filter-card">
                    <Row
                        gutter={[
                            16,
                            16
                        ]}
                        align="middle"
                    >
                        <Col flex="320px">
                            <RangePicker
                                value={
                                    dateRange
                                }
                                onChange={
                                    setDateRange
                                }
                                style={{
                                    width:
                                        '100%'
                                }}
                            />
                        </Col>

                        <Col flex="260px">
                            <Input
                                value={
                                    filters.search
                                }
                                prefix={
                                    <SearchOutlined />
                                }
                                placeholder="Поиск по заявке, ТТ или БС"
                                onChange={event =>
                                    setFilters(
                                        previous => ({
                                            ...previous,

                                            search:
                                                event
                                                    .target
                                                    .value
                                        })
                                    )
                                }
                                onPressEnter={
                                    fetchReport
                                }
                            />
                        </Col>

                        <Col flex="220px">
                            <Select
                                allowClear
                                showSearch
                                placeholder="Статус"
                                value={
                                    filters.status
                                }
                                options={
                                    report.options
                                        .statuses
                                        .map(value => ({
                                            value,
                                            label:
                                                value
                                        }))
                                }
                                onChange={value =>
                                    setFilters(
                                        previous => ({
                                            ...previous,
                                            status:
                                                value
                                        })
                                    )
                                }
                                style={{
                                    width:
                                        '100%'
                                }}
                            />
                        </Col>

                        <Col flex="220px">
                            <Select
                                allowClear
                                showSearch
                                placeholder="Вид работ"
                                value={
                                    filters.workType
                                }
                                options={
                                    report.options
                                        .workTypes
                                        .map(value => ({
                                            value,
                                            label:
                                                value
                                        }))
                                }
                                onChange={value =>
                                    setFilters(
                                        previous => ({
                                            ...previous,
                                            workType:
                                                value
                                        })
                                    )
                                }
                                style={{
                                    width:
                                        '100%'
                                }}
                            />
                        </Col>

                        <Col flex="160px">
                            <Button
                                block
                                onClick={
                                    resetFilters
                                }
                            >
                                Сбросить
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Spin spinning={loading}>
                    <Row
                        gutter={[
                            16,
                            16
                        ]}
                        className="customer-statistics"
                    >
                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Всего заявок"
                                    value={
                                        report.summary
                                            .total ||
                                        0
                                    }
                                    prefix={
                                        <FileSearchOutlined />
                                    }
                                />
                            </Card>
                        </Col>

                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="В работе"
                                    value={
                                        report.summary
                                            .active ||
                                        0
                                    }
                                    prefix={
                                        <ClockCircleOutlined />
                                    }
                                />
                            </Card>
                        </Col>

                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Осмечено"
                                    value={
                                        report.summary
                                            .estimated ||
                                        0
                                    }
                                    prefix={
                                        <CheckCircleOutlined />
                                    }
                                />
                            </Card>
                        </Col>

                        <Col span={6}>
                            <Card>
                                <Statistic
                                    title="Стоимость работ (прогнозируемая)"
                                    value={
                                        report.summary
                                            .incomeAmount ||
                                        0
                                    }
                                    precision={2}
                                    suffix="₽"
                                />
                            </Card>
                        </Col>
                    </Row>

                    <Row
                        gutter={[
                            16,
                            16
                        ]}
                        style={{
                            marginTop:
                                16
                        }}
                    >
                        <Col span={16}>
    <Card
    title="Заявки по дням"
    extra={
        <Text strong>
            Всего: {
                selectedChartStatuses.length
                    ? dailyChartData.reduce(
                        (sum, item) =>
                            sum +
                            Number(
                                item.total ||
                                0
                            ),
                        0
                    )
                    : report.summary.total ||
                    0
            }
        </Text>
    }
    className="customer-chart-card"
>
    {selectedChartStatuses.length > 0 && (
    <div
        style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            maxWidth: '100%',
            overflow: 'hidden'
        }}
    >
        <Text
            type="secondary"
            style={{
                marginRight: 4
            }}
        >
            Выбрано:
        </Text>

        {selectedChartStatuses.map(
            status => (
                <Tag
                    key={status}
                    closable
                    color={
                        getStatusColor(
                            status
                        )
                    }
                    onClose={event => {
                        event.preventDefault();

                        toggleChartStatus(
                            status
                        );
                    }}
                    style={{
                        margin: 0,
                        maxWidth: 260,
                        whiteSpace:
                            'normal',
                        lineHeight:
                            '20px'
                    }}
                >
                    {status}
                </Tag>
            )
        )}
    </div>
)}
        {dailyChartData.length ? (
            <ResponsiveContainer
                width="100%"
                height={430}
            >
                <BarChart
                    data={
                        dailyChartData
                    }
                    margin={{
        top: 10,
        right: 20,
        left: 0,
        bottom: 90
    }}
                >
                    <CartesianGrid
                        strokeDasharray="3 3"
                    />

                    <XAxis
                        dataKey="date"
                        minTickGap={20}
                        height={45}
                        tickFormatter={
                            value =>
                                dayjs(value)
                                    .format(
                                        'DD.MM'
                                    )
                        }
                    />

                    <YAxis
                        allowDecimals={false}
                    />

                    <Tooltip
    labelFormatter={value =>
        dayjs(value).format(
            'DD.MM.YYYY'
        )
    }
    content={({
        active,
        label,
        payload
    }) => {
        if (
            !active ||
            !payload?.length
        ) {
            return null;
        }

        const chartRow =
            payload[0]?.payload ||
            {};

        return (
            <div
                style={{
                    background: '#fff',
                    border:
                        '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: 12,
                    boxShadow:
                        '0 4px 12px rgba(0, 0, 0, 0.12)',
                    maxWidth: 320
                }}
            >
                <Text strong>
                    {dayjs(label).format(
                        'DD.MM.YYYY'
                    )}
                </Text>

                <div
                    style={{
                        marginTop: 8,
                        marginBottom: 8
                    }}
                >
                    <Text strong>
                        Всего: {
                            chartRow.total ||
                            0
                        }
                    </Text>
                </div>

                <Space
                    direction="vertical"
                    size={4}
                    style={{
                        width: '100%'
                    }}
                >
                    {payload
                        .filter(item => {
                            return Number(
                                item.value
                            ) > 0;
                        })
                        .map(item => (
                            <div
                                key={
                                    item.dataKey
                                }
                                style={{
                                    display:
                                        'flex',
                                    justifyContent:
                                        'space-between',
                                    gap: 16
                                }}
                            >
                                <Space size={6}>
                                    <span
                                        style={{
                                            display:
                                                'inline-block',
                                            width: 10,
                                            height: 10,
                                            borderRadius:
                                                2,
                                            background:
                                                item.color
                                        }}
                                    />

                                    <Text>
                                        {item.name}
                                    </Text>
                                </Space>

                                <Text strong>
                                    {item.value}
                                </Text>
                            </div>
                        ))}
                </Space>
            </div>
        );
    }}
/>

                    <Legend
    verticalAlign="bottom"
    align="center"
    wrapperStyle={{
        paddingTop: 20,
        lineHeight: '24px',
        maxWidth: '100%',
        whiteSpace: 'normal'
    }}
/>

                    {displayedChartStatuses.map(
    status => (
        <Bar
            key={status}
            dataKey={
                getStatusDataKey(
                    status
                )
            }
            name={status}
            fill={
                getStatusColor(
                    status
                )
            }
            stackId="statuses"
            maxBarSize={55}
        />
    )
)}
                </BarChart>
            </ResponsiveContainer>
        ) : (
            <Empty
                description={
                    selectedChartStatuses
                        ? `Нет заявок со статусом «${selectedChartStatuses.join(', ')}»`
                        : 'Нет заявок за выбранный период'
                }
            />
        )}
    </Card>
</Col>

                        <Col span={8}>
                            <Card
    title="Заявки по статусам"
    extra={
        selectedChartStatuses.length ? (
            <Button
                type="link"
                size="small"
                onClick={() => {
                    setSelectedChartStatuses(
                        []
                    );
                }}
            >
                Показать все
            </Button>
        ) : null
    }
    className="customer-status-card"
>
                                <Space
                                    direction="vertical"
                                    size={14}
                                    style={{
                                        width:
                                            '100%'
                                    }}
                                >
                                    {report.charts.statuses.map(item => {
    const percent =
        report.summary.total > 0
            ? Math.round(
                item.count /
                report.summary.total *
                100
            )
            : 0;

    const isSelected =
    selectedChartStatuses.some(status => {
        return (
            normalizeStatus(status) ===
            normalizeStatus(item.status)
        );
    });

    return (
        <div
            key={item.status}
            role="button"
            tabIndex={0}
            onClick={() => {
    toggleChartStatus(
        item.status
    );
}}
            onKeyDown={event => {
    if (
        event.key === 'Enter' ||
        event.key === ' '
    ) {
        event.preventDefault();

        toggleChartStatus(
            item.status
        );
    }
}}
            style={{
                cursor: 'pointer',
                padding: 10,
                borderRadius: 8,

                border:
                    isSelected
                        ? `2px solid ${
                            getStatusColor(
                                item.status
                            )
                        }`
                        : '1px solid transparent',

                background:
                    isSelected
                        ? `${getStatusColor(
                            item.status
                        )}10`
                        : 'transparent',

                transition:
                    'all 0.2s'
            }}
        >
            <div className="customer-status-line">
                <StatusTag
                    status={
                        item.status
                    }
                />

                <Space>
                    <Text strong>
                        {item.count}
                    </Text>

                    <Text type="secondary">
                        {percent}%
                    </Text>
                </Space>
            </div>

            <Progress
                percent={percent}
                showInfo={false}
                strokeColor={
                    getStatusColor(
                        item.status
                    )
                }
            />
        </div>
    );
})}
                                </Space>
                            </Card>
                        </Col>
                    </Row>

                    <Card
                        title="Все заявки"
                        style={{
                            marginTop:
                                16
                        }}
                    >
                        <Table
                            rowKey="id"
                            columns={
                                taskColumns
                            }
                            dataSource={
                                report.tasks
                            }
                            scroll={{
                                x:
                                    1650,

                                y:
                                    520
                            }}
                            pagination={{
                                pageSize:
                                    20,

                                showSizeChanger:
                                    true,

                                pageSizeOptions: [
                                    20,
                                    50,
                                    100
                                ]
                            }}
                        />
                    </Card>

                    <Card
                        title="Осмеченные заявки"
                        style={{
                            marginTop:
                                16
                        }}
                    >
                        <Table
                            rowKey="id"
                            columns={
                                estimatedTaskColumns
                            }
                            dataSource={
                                report
                                    .estimatedTasks
                            }
                            scroll={{
                                x:
                                    1650,

                                y:
                                    520
                            }}
                            expandable={{
                                expandedRowRender:
                                    task => (
                                        <div className="customer-vdr-block">
                                            <div className="customer-vdr-summary">
                                                <Text strong>
                                                    Доходная сторона ВДР
                                                </Text>

                                                <Text strong>
                                                    Итого:{' '}
                                                    {formatMoney(
                                                        task.incomeAmount
                                                    )}
                                                </Text>
                                            </div>

                                            <Table
                                                rowKey="id"
                                                columns={
                                                    incomeColumns
                                                }
                                                dataSource={
                                                    task.incomeInvoices
                                                }
                                                pagination={
                                                    false
                                                }
                                                size="small"
                                                scroll={{
                                                    x:
                                                        1200
                                                }}
                                            />
                                        </div>
                                    )
                            }}
                            pagination={{
                                pageSize:
                                    20
                            }}
                        />
                    </Card>
                </Spin>

                <Modal
    open={
        taskModalOpen
    }
    width="94vw"
    style={{
        top:
            20
    }}
    title={
        <Space wrap>
            <span>
                Заявка №
                {
                    modalTask.id ||
                    modalTask.external_id ||
                    selectedTask?.externalId ||
                    '—'
                }
            </span>

            <StatusTag
                status={
                    modalTask.status_name ||
                    modalTask.status?.name ||
                    selectedTask?.status
                }
            />
        </Space>
    }
    footer={
        <Button
            onClick={closeTaskModal}
        >
            Закрыть
        </Button>
    }
    onCancel={closeTaskModal}
    destroyOnClose
    styles={{
        body: {
            maxHeight:
                'calc(100vh - 140px)',

            overflowY:
                'auto'
        }
    }}
>
    <Spin
        spinning={
            taskModalLoading
        }
    >
        <Row
            gutter={[
                16,
                16
            ]}
        >
            <Col span={16}>
                <Card
                    size="small"
                    title="Информация о заявке"
                >
                    <Descriptions
                        bordered
                        column={3}
                        size="small"
                    >
                        <Descriptions.Item label="ID">
                            {
                                modalTask.id ||
                                modalTask.external_id ||
                                '—'
                            }
                        </Descriptions.Item>
                        <Descriptions.Item label="Исполнитель">
    {executorName}
</Descriptions.Item>

<Descriptions.Item label="Соисполнитель">
    {formatTaskValue(
        coexecutorName
    )}
</Descriptions.Item>

<Descriptions.Item label="Соисполнитель 2">
    {formatTaskValue(
        secondCoexecutorName
    )}
</Descriptions.Item>

<Descriptions.Item label="Прибыл на объект">
    {formatTaskDateTime(
        siteAppearanceDate
    )}
</Descriptions.Item>

<Descriptions.Item label="Покинул объект">
    {formatTaskDateTime(
        siteLeavingDate
    )}
</Descriptions.Item>

                        <Descriptions.Item label="Статус">
                            <StatusTag
                                status={
                                    modalTask.status_name ||
                                    modalTask.status?.name ||
                                    selectedTask?.status
                                }
                            />
                        </Descriptions.Item>

                        <Descriptions.Item label="Вид работ">
                            {
                                modalTask.news_type_name ||
                                modalTask.type?.name ||
                                selectedTask?.workType ||
                                '—'
                            }
                        </Descriptions.Item>

                        <Descriptions.Item label="Заказчик">
                            {
                                getCustomField(
                                    modalTask,
                                    'Zakazchik'
                                ) ||
                                selectedTask?.customer ||
                                '—'
                            }
                        </Descriptions.Item>

                        <Descriptions.Item label="Номер ТТ">
                            {
                                getCustomField(
                                    modalTask,
                                    'Nomer_TT'
                                ) ||
                                selectedTask?.ttNumber ||
                                '—'
                            }
                        </Descriptions.Item>

                        <Descriptions.Item label="Номер БС">
                            {
                                getCustomField(
                                    modalTask,
                                    'Nomer_BS'
                                ) ||
                                selectedTask?.bsNumber ||
                                '—'
                            }
                        </Descriptions.Item>

                        <Descriptions.Item label="Создана">
                            {formatTaskDateTime(
                                modalTask.news_date ||
                                selectedTask?.createdAt
                            )}
                        </Descriptions.Item>

                        <Descriptions.Item label="Срок">
                            {formatTaskDateTime(
                                modalTask.due_date
                            )}
                        </Descriptions.Item>

                        <Descriptions.Item label="Осметил">
                            {
                                modalTask
                                    .filling_user_fio ||
                                modalTask
                                    .filling_user_name ||
                                '—'
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
                            label="Название"
                            span={3}
                        >
                            {
                                modalTask.title ||
                                '—'
                            }
                        </Descriptions.Item>

                        <Descriptions.Item
    label="Задание"
    span={3}
>
    <div
        style={{
            whiteSpace: 'pre-wrap',
            padding: 8,
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 4
        }}
    >
        {taskDescription}
    </div>
</Descriptions.Item>
<Descriptions.Item
    label="Описание выполненных работ"
    span={3}
>
    <div
        style={{
            whiteSpace: 'pre-wrap',
            padding: 8,
            minHeight: 80,
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 4
        }}
    >
        {formatTaskValue(
            completedWorksDescription
        )}
    </div>
</Descriptions.Item>
                    </Descriptions>
                </Card>

                <Card
                    size="small"
                    title="Осмечивание"
                    style={{
                        marginTop:
                            16
                    }}
                >
                    <Row
                        gutter={[
                            16,
                            16
                        ]}
                        style={{
                            marginBottom:
                                16
                        }}
                    >
                        <Col span={8}>
                            <Statistic
                                title="Доходных строк"
                                value={
                                    estimation
                                        .income_rows_count ||
                                    0
                                }
                            />
                        </Col>

                        <Col span={8}>
                            <Statistic
                                title="Учитываемых строк"
                                value={
                                    estimation
                                        .included_income_rows_count ||
                                    0
                                }
                            />
                        </Col>

                        <Col span={8}>
                            <Statistic
                                title="Итого"
                                value={
                                    estimation
                                        .total_income ||
                                    0
                                }
                                precision={2}
                                suffix="₽"
                            />
                        </Col>
                    </Row>

                    <Table
                        rowKey="id"
                        columns={
                            customerEstimateColumns
                        }
                        dataSource={
                            incomeInvoices
                        }
                        pagination={
                            false
                        }
                        size="small"
                        scroll={{
                            x:
                                1200
                        }}
                        locale={{
                            emptyText:
                                'Доходная ВДР отсутствует'
                        }}
                    />
                
                </Card>
                <Card
    size="small"
    title="Согласование сметы"
    style={{
        marginTop:
            16
    }}
>
    {currentEstimateReview && (
    <Alert
        type={
            currentEstimateReview.status ===
            'accepted'
                ? 'success'
                : currentEstimateReview.status ===
                  'rejected'
                    ? 'error'
                    : 'warning'
        }
        showIcon
        message={
            `Текущий статус: ${
                getEstimateStatusLabel(
                    currentEstimateReview.status
                )
            }`
        }
        description={
            <Space
                direction="vertical"
                size={2}
            >
                <Text>
                    Заказчик:{' '}
                    <Text strong>
                        {getUserFio(
                            currentEstimateReview
                                .customer
                        )}
                    </Text>
                </Text>

                <Text>
                    Ответственный:{' '}
                    <Text strong>
                        {getUserFio(
                            currentEstimateReview
                                .recipient
                        )}
                    </Text>
                </Text>

                <Text type="secondary">
                    Отправлено:{' '}
                    {currentEstimateReview
                        .submitted_at
                        ? dayjs(
                            currentEstimateReview
                                .submitted_at
                        ).format(
                            'DD.MM.YYYY HH:mm'
                        )
                        : '—'}
                </Text>

                {currentEstimateReview
                    .customer_comment && (
                    <Text>
                        Комментарий:{' '}
                        {
                            currentEstimateReview
                                .customer_comment
                        }
                    </Text>
                )}
            </Space>
        }
        style={{
            marginBottom: 16
        }}
    />
)}

{estimateReviewHistory.length > 0 && (
    <Card
        size="small"
        title="История согласований"
        style={{
            marginBottom: 16
        }}
    >
        <Space
            direction="vertical"
            size={12}
            style={{
                width: '100%'
            }}
        >
            {estimateReviewHistory.map(
                (review, reviewIndex) => (
                    <Card
                        key={review.id}
                        size="small"
                        type="inner"
                        title={
                            <Space wrap>
                                <Text strong>
                                    Обращение №
                                    {review.id}
                                </Text>

                                <Tag
                                    color={
                                        review.status ===
                                        'accepted'
                                            ? 'green'
                                            : review.status ===
                                              'rejected'
                                                ? 'red'
                                                : review.status ===
                                                  'partially_accepted'
                                                    ? 'orange'
                                                    : 'blue'
                                    }
                                >
                                    {
                                        getEstimateStatusLabel(
                                            review.status
                                        )
                                    }
                                </Tag>

                                {reviewIndex === 0 && (
                                    <Tag color="purple">
                                        Текущее
                                    </Tag>
                                )}
                            </Space>
                        }
                        extra={
                            <Text type="secondary">
                                {dayjs(
                                    review.submitted_at ||
                                    review.created_at
                                ).format(
                                    'DD.MM.YYYY HH:mm'
                                )}
                            </Text>
                        }
                    >
                        <Descriptions
                            size="small"
                            bordered
                            column={2}
                        >
                            <Descriptions.Item
                                label="Заказчик"
                            >
                                {getUserFio(
                                    review.customer
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item
                                label="Ответственный"
                            >
                                {getUserFio(
                                    review.recipient
                                )}
                            </Descriptions.Item>

                            <Descriptions.Item
                                label="Комментарий заказчика"
                                span={2}
                            >
                                {review.customer_comment ||
                                    '—'}
                            </Descriptions.Item>

                            {review.employee_comment && (
                                <Descriptions.Item
                                    label="Ответ сотрудника"
                                    span={2}
                                >
                                    {
                                        review.employee_comment
                                    }
                                </Descriptions.Item>
                            )}

                            <Descriptions.Item
                                label="Дата решения"
                                span={2}
                            >
                                {review.resolved_at
                                    ? dayjs(
                                        review.resolved_at
                                    ).format(
                                        'DD.MM.YYYY HH:mm'
                                    )
                                    : 'Ещё не рассмотрено'}
                            </Descriptions.Item>
                        </Descriptions>

                        {Array.isArray(
                            review.items
                        ) &&
                        review.items.length > 0 && (
                            <Table
                                style={{
                                    marginTop: 12
                                }}
                                size="small"
                                pagination={false}
                                rowKey="id"
                                dataSource={
                                    review.items
                                }
                                scroll={{
                                    x: 1200
                                }}
                                columns={[
                                    {
                                        title:
                                            'Наименование',

                                        key:
                                            'rate_name',

                                        width:
                                            260,

                                        render:
                                            (_, item) =>
                                                item.invoice
                                                    ?.rate_name ||
                                                '—'
                                    },

                                    {
                                        title:
                                            'Было кол-во',

                                        dataIndex:
                                            'original_quantity',

                                        key:
                                            'original_quantity',

                                        width:
                                            110
                                    },

                                    {
                                        title:
                                            'Предложено кол-во',

                                        dataIndex:
                                            'proposed_quantity',

                                        key:
                                            'proposed_quantity',

                                        width:
                                            140,

                                        render:
                                            value =>
                                                value ??
                                                'Без изменений'
                                    },

                                    {
                                        title:
                                            'Была цена',

                                        dataIndex:
                                            'original_price',

                                        key:
                                            'original_price',

                                        width:
                                            130,

                                        render:
                                            formatMoney
                                    },

                                    {
                                        title:
                                            'Предложена цена',

                                        dataIndex:
                                            'proposed_price',

                                        key:
                                            'proposed_price',

                                        width:
                                            150,

                                        render:
                                            value =>
                                                value !==
                                                    null &&
                                                value !==
                                                    undefined
                                                    ? formatMoney(
                                                        value
                                                    )
                                                    : 'Без изменений'
                                    },

                                    {
                                        title:
                                            'Комментарий заказчика',

                                        dataIndex:
                                            'customer_comment',

                                        key:
                                            'customer_comment',

                                        width:
                                            250,

                                        render:
                                            value =>
                                                value ||
                                                '—'
                                    },

                                    {
                                        title:
                                            'Решение',

                                        dataIndex:
                                            'decision',

                                        key:
                                            'decision',

                                        width:
                                            150,

                                        render:
                                            value => (
                                                <Tag
                                                    color={
                                                        value ===
                                                        'accepted'
                                                            ? 'green'
                                                            : value ===
                                                              'rejected'
                                                                ? 'red'
                                                                : 'blue'
                                                    }
                                                >
                                                    {
                                                        ESTIMATE_ITEM_DECISION_LABELS[
                                                            value
                                                        ] ||
                                                        value
                                                    }
                                                </Tag>
                                            )
                                    },

                                    {
                                        title:
                                            'Рассмотрел',

                                        key:
                                            'decidedBy',

                                        width:
                                            200,

                                        render:
                                            (_, item) =>
                                                getUserFio(
                                                    item.decidedBy
                                                )
                                    },

                                    {
                                        title:
                                            'Ответ сотрудника',

                                        dataIndex:
                                            'employee_comment',

                                        key:
                                            'employee_comment',

                                        width:
                                            250,

                                        render:
                                            value =>
                                                value ||
                                                '—'
                                    },

                                    {
                                        title:
                                            'Дата решения',

                                        dataIndex:
                                            'decided_at',

                                        key:
                                            'decided_at',

                                        width:
                                            160,

                                        render:
                                            value =>
                                                value
                                                    ? dayjs(
                                                        value
                                                    ).format(
                                                        'DD.MM.YYYY HH:mm'
                                                    )
                                                    : '—'
                                    }
                                ]}
                            />
                        )}
                    </Card>
                )
            )}
        </Space>
    </Card>
)}

    <Input.TextArea
        rows={4}
        value={
            estimateComment
        }
        placeholder="Общий комментарий по смете"
        onChange={event =>
            setEstimateComment(
                event.target.value
            )
        }
    />

    <div
        style={{
            marginTop:
                12,
            textAlign:
                'right'
        }}
    >
        <Button
            type="primary"
            danger
            loading={
                estimateSending
            }
            onClick={
                submitEstimateCorrections
            }
        >
            Отправить замечания
        </Button>
    </div>
</Card>
            </Col>

            <Col span={8}>
                <Card
                    size="small"
                    title={
                        `Фотографии (${
                            modalPhotos.length
                        })`
                    }
                >
                    {modalPhotos.length ? (
                        <div className="customer-task-gallery">
                            {modalPhotos.map(
                                photo => (
                                    <a
    key={photo.num}
    href={
        taskOriginalUrls[
            photo.num
        ] ||
        taskPhotoUrls[
            photo.num
        ]
    }
    data-fancybox="task-gallery"
    data-caption={
        photo.description ||
        `Фото №${photo.num}`
    }
    className="customer-task-gallery-item"
>
    {taskPhotoUrls[
        photo.num
    ] ? (
        <img
            src={
                taskPhotoUrls[
                    photo.num
                ]
            }
            alt={
                photo.description ||
                `Фото ${photo.num}`
            }
            className="customer-task-gallery-image"
        />
    ) : (
        <div className="customer-task-gallery-loading">
            <Spin />
        </div>
    )}

    <div className="customer-task-gallery-caption">
        <Text strong>
            Фото №{photo.num}
        </Text>

        {photo.description ? (
            <Text type="secondary">
                {photo.description}
            </Text>
        ) : null}

        <Text type="secondary">
            {formatTaskDateTime(
                photo.create_date
            )}
        </Text>
    </div>
</a>
                                )
                            )}
                        </div>
                    ) : (
                        <Empty
                            image={
                                Empty
                                    .PRESENTED_IMAGE_SIMPLE
                            }
                            description="Фотографии отсутствуют"
                        />
                    )}
                </Card>
            </Col>
        </Row>
    </Spin>
</Modal>
            </div>
        </div>
    );
};

export default CustomerCabinetPage;